import React, { MouseEvent, ReactNode, useRef, useState } from "react";

import "./ACPanelResize.less";

type ACPanelResizeProps = {
  children: ReactNode | ReactNode[];
  disabled?: boolean;
  resizeFrom: "left" | "right" | "both";
  onChange: (width: number) => void;
};

const prefixCls = "ac-panel-resize";

export default function ACPanelResize({
  children,
  disabled,
  resizeFrom,
  onChange,
}: ACPanelResizeProps) {
  const wrapperRef = useRef<HTMLDivElement | null>();
  const [top, setTop] = useState(0);
  const mousemove = (e: MouseEvent<HTMLDivElement>) => {
    // math.max as 15 and not 0 because stick translate top is -15px
    setTop(
      Math.max(
        15,
        e.pageY -
          e.currentTarget.offsetTop -
          (wrapperRef.current?.getBoundingClientRect().top ?? 0),
      ),
    );
  };
  const mousedown = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    let isMouseDown = true;
    const mousemove = (e: GlobalEventHandlersEventMap["mousemove"]) => {
      e.stopPropagation();
      if (!wrapperRef.current) {
        return;
      }
      const wrapperBound = wrapperRef.current.getBoundingClientRect();
      const center = wrapperBound.left + wrapperBound.width / 2;
      if (isMouseDown) {
        if (resizeFrom === "both") {
          onChange(Math.abs(e.pageX - center) * 2);
        } else if (resizeFrom === "left") {
          onChange(
            Math.abs(e.pageX - (wrapperBound.left + wrapperBound.width)),
          );
        } else {
          onChange(Math.abs(e.pageX - wrapperBound.left));
        }
      }
    };
    const mouseup = () => {
      window.addEventListener("mousemove", mousemove);
      isMouseDown = false;
    };
    window.addEventListener("mousemove", mousemove);
    window.addEventListener("mouseup", mouseup);
  };
  if (disabled) {
    return <>{children}</>;
  }
  return (
    <div
      className={`${prefixCls}-wrapper`}
      ref={(r) => (wrapperRef.current = r)}
    >
      {children}
      {["left", "both"].includes(resizeFrom) && (
        <div
          className={`${prefixCls}-handle-left`}
          onMouseMove={mousemove}
          onMouseDown={mousedown}
          data-type="left"
        >
          <span className={`${prefixCls}-handle-permanent-stick-right`} />
          <span className={`${prefixCls}-handle-left-stick`} style={{ top }} />
          <span className={`${prefixCls}-handle-right-stick`} style={{ top }} />
        </div>
      )}
      {["right", "both"].includes(resizeFrom) && (
        <div
          className={`${prefixCls}-handle-right`}
          onMouseMove={mousemove}
          onMouseDown={mousedown}
          data-type="right"
        >
          <span className={`${prefixCls}-handle-permanent-stick-left`} />
          <span className={`${prefixCls}-handle-left-stick`} style={{ top }} />
          <span className={`${prefixCls}-handle-right-stick`} style={{ top }} />
        </div>
      )}
    </div>
  );
}
